import { CommandBar, Selection, ConstrainMode, DetailsListLayoutMode, IColumn, ICommandBarItemProps, ICommandBarStyles, IIconProps, mergeStyleSets, ScrollablePane, ScrollbarVisibility, SelectionMode, ShimmeredDetailsList, Stack, Icon, StackItem, Persona, PersonaSize, IDetailsFooterProps, Label, IStackStyles, IDetailsRowProps } from '@fluentui/react';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ContentManagerCopyMoveModeEnum } from '../../../src/taskpane/components/enums/ContentManagerCopyMoveModeEnum';
import { RootState } from '../../../src/taskpane/components/_v2/redux/storeMain';
import { IFileToUpload } from '../../../src/taskpane/interfaces/IFileToUpload';
import { resetContentManagerDirectory, setContentManagerExchangeProgress, setContentManagerMoveTo, setContentManagerSearchValue } from '../../../src/taskpane/redux/modules/contentManagerDirectory';
import { addUploadedFiles, setFiles } from '../../../src/taskpane/redux/modules/file';
import { LanguageContext } from '../../../utilities/LocalizationModule';
import { CMConfirmModal } from '../../Panels/CMConfirmModal';
import dayjs from 'dayjs';
import 'dayjs/locale/nl'; // import locale
import 'dayjs/locale/de';
import { logger } from '../../../utilities/logger';
import generalModule from '../../../utilities/GeneralModule';
import ExpanderWithSubtitle from '../../../src/taskpane/components/directoryComponents/ExpanderWithSubtitle';
import { Fileview } from '../../../src/taskpane/components/_v2/components/Explorer/Fileview/Fileview';
import { isMobile } from '../../../src/taskpane/styles/mobileStyles';
import { WebPanel } from '../../../src/taskpane/components/_v2/_shared/WebPanel/WebPanel';
import MultipleFilesSeleted from '../../../src/taskpane/components/panels/MultipleFilesSeleted';
import { removeExtension } from '../../../src/taskpane/components/helpers/stringHelper';
import { ExchangePreview } from './ExchangePreview';
import { IExchangeMessages, getExchangeMessageAttachments, getExchangeMessages } from '../../../src/taskpane/services/ExchangeService';
import { DocubirdCache, displayHeight } from '../../../src/taskpane/config/constants';
import { resetWorkingDirectories } from '../../../src/taskpane/redux/modules/workingDirectory';
import { CacheHelper } from '../../../utilities/CacheHelper';
import { DragAndDropHelper } from '../../../utilities/DragAndDropHelper';
const searchIconSrc = require("../../../assets/empty_search.svg");

const classNames = mergeStyleSets({
  wrapper: {
    height: displayHeight,
    position: "relative",
  }
});

const commandBarStyle: Partial<ICommandBarStyles> = {
  root: {
    borderTop: '1px solid rgb(237, 235, 233);',
    borderBottom: '1px solid rgb(237, 235, 233);',
    paddingLeft: 0,
    paddingRight: 10,
    paddingBottom: 3,
    paddingTop: 3
  }
};

const noResultsStackStyles: Partial<IStackStyles> = {
  root: { width: "100%", height: "calc(100vh - 250px)", margin: 0, padding: 0, overflow: "auto" }
};

const saveSelected: IIconProps = { iconName: "Save" };
const saveFolder: IIconProps = { iconName: "SaveAll" };
const cancelIcon: IIconProps = { iconName: "Cancel" };

interface ExchangeContentProps { }

export const ExchangeContent: React.FC<ExchangeContentProps> = ({ }) => {
  const languageStrings = React.useContext(LanguageContext);

  const MAX_SELECTED_FILES = 50;

  const dispatch = useDispatch();

  const workingDirectories = useSelector((state: RootState) => state.workingDirectories?.workingDirectories);
  const exchangeFolderId = useSelector((state: RootState) => state.contentManagerDirectory?.exchange.selectedFolderId);
  const exchangeAccount = useSelector((state: RootState) => state.contentManagerDirectory?.exchange.selectedAccount);
  const moveTo = useSelector((state: RootState) => state.contentManagerDirectory.moveTo);
  const contentManagerSearchValue = useSelector((state: RootState) => state.contentManagerDirectory?.searchValue);
  const files = useSelector((state: RootState) => state.files.files);
  const currentDirectory = useSelector((state: RootState) => state.workingDirectories.workingDirectories.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);
  const searchValue = useSelector((state: RootState) => state.search?.value)?.value;
  const autoSelectAttachments = useSelector((state: RootState) => state.user?.settings.autoSelectAttachments);

  const [isLoading, setIsLoading] = React.useState(true);
  const [columns, setColumns] = React.useState<IColumn[]>(null);
  const [items, setItems] = React.useState<IExchangeMessages>({ messages: [] });
  const [commandItems, setCommandItems] = React.useState<ICommandBarItemProps[]>();
  const [commandFarItems, setCommandFarItems] = React.useState<ICommandBarItemProps[]>();
  const [showConfirmMoveToDialog, setShowConfirmMoveToDialog] = React.useState<boolean>(false);
  const [showMaxFilesSelectedDialog, setShowMaxFilesSelectedDialog] = React.useState<boolean>(false);
  const [selectedItems, setSelectedItems] = React.useState<any[]>([]);
  const [selectionCleared, setSelectionCleared] = React.useState<boolean>(false);
  const [isFetchingAttachments, setIsFetchingAttachments] = React.useState(false);
  const [fileViews, setFileViews] = React.useState<JSX.Element[]>([]);
  const [isMultipleFilesSelectedPanelOpen, setIsMultipleFilesSelectedPanelOpen] = React.useState(false);
  const [sortColumn, setSortColumn] = React.useState("receivedDateTime");
  const [sortAscDesc, setSortAscDesc] = React.useState("desc");
  const [updateEmails, setUpdateEmails] = React.useState("");

  const isFirstLoad = React.useRef<boolean>(true);
  const isFetchingNextPage = React.useRef<boolean>(false);

  const _selection = React.useRef(new Selection({
    onSelectionChanged: () => _getSelectionDetails()
  }));

  const _getSelectionDetails = () => {
    setSelectedItems(_selection.current.getSelection());
  };

  React.useEffect(() => {
    if (exchangeFolderId) {
      fetchEmails();
    }
  }, [exchangeFolderId]);

  React.useEffect(() => {
    if (updateEmails) {
      fetchEmails();
    }
  }, [updateEmails]);

  React.useEffect(() => {
    if (!isFirstLoad.current) {
      fetchEmails();
    }
  }, [contentManagerSearchValue, searchValue]);

  const fetchEmails = async () => {
    setIsLoading(true);
    try {
      let filter: string;
      if (generalModule.isInConentMangerMode()) {
        filter = contentManagerSearchValue ? contentManagerSearchValue.value : "";
      } else {
        filter = searchValue ? searchValue.value : "";
      }

      setMessageItems(await getExchangeMessages(exchangeFolderId, filter, sortColumn, sortAscDesc, exchangeAccount));
    } catch (error) {
      logger(error, "ExchangeDirectory.tsx (fetchEmails)");
    }

    setIsLoading(false);

    if (isFirstLoad.current) {
      isFirstLoad.current = false;
    }
  }

  const fetchNextPageEmails = async () => {
    if (!isFetchingNextPage.current && items.nextLink?.length > 0) {
      isFetchingNextPage.current = true;
      try {
        setMessageItems(await getExchangeMessages(null, null, null, null, null, items.nextLink), false);
      } catch (error) {
        logger(error, "ExchangeDirectory.tsx (fetchNextPageEmails)");
      }
      isFetchingNextPage.current = false;
    }
  }

  const setMessageItems = (messagesResult: IExchangeMessages, isFirstPage: boolean = true) => {
    if (Array.isArray(messagesResult?.messages)) {
      const messageItems = messagesResult.messages.map(message => {
        return {
          ...message,
          selected: false
        }
      });

      if (messagesResult?.nextLink?.length > 0) {
        messageItems.push(null);
      }

      if (isFirstPage) {
        setItems({ messages: messageItems, nextLink: messagesResult.nextLink });
      }
      else {
        setItems(items => { return { messages: items.messages.filter(msg => msg).concat(messageItems), nextLink: messagesResult.nextLink } });
      }
    }
  }

  const selectItem = (item: any) => {
    try {
      const newItems = [...items.messages];

      const selectedFile = newItems.find((c) => c.id === item.id);

      if (selectedFile) {
        selectedFile.selected = selectedFile.selected ? !selectedFile.selected : true;
        if (selectedFile.selected) {
          addEmailWithAttachmentToRedux(selectedFile);
        }
        else {
          dispatch(setFiles(files.filter(x => x.graphMailItemId !== selectedFile.id)));
        }
      }

      setItems(items => { return { messages: newItems, nextLink: items.nextLink } });
    } catch (error) {
      logger(error, "ExchangeDirectory.tsx (selectItem)");
    }
  };

  const addEmailWithAttachmentToRedux = async (selectedFile: any) => {
    let filesToUpload: IFileToUpload[] = [
      {
        id: selectedFile.id,
        ewsId: null,
        name: selectedFile.subject || languageStrings.NoSubject,
        extension: "eml",
        isAttachment: false,
        isSelected: true,
        isDuplicate: false,
        originalName: selectedFile.subject || languageStrings.NoSubject,
        lastModifiedDateTime: selectedFile.receivedDateTime,
        isGraphMailItem: true,
        graphMailItemId: selectedFile.id
      }
    ];

    if (selectedFile.hasAttachments) {
      const attachments = await getExchangeMessageAttachments(exchangeAccount, selectedFile.id);
      if (attachments?.length > 0) {
        for (let index = 0; index < attachments.length; index++) {
          let attachment = attachments[index];
          const attachmentToUpload: IFileToUpload = {
            id: attachment.id,
            ewsId: null,
            name: removeExtension(attachment.name),
            extension: getFileExtension(attachment.name),
            isAttachment: true,
            isSelected: autoSelectAttachments,
            isDuplicate: false,
            originalName: removeExtension(attachment.name),
            lastModifiedDateTime: attachment.lastModifiedDateTime,
            isGraphMailItem: true,
            graphMailItemId: selectedFile.id
          }

          filesToUpload.push(attachmentToUpload);
        }
      }
    }

    dispatch(addUploadedFiles(filesToUpload));
  }

  React.useEffect(() => {
    if (!generalModule.isInConentMangerMode()) {
      setFileViews(
        items.messages.map((item, index) => (
          <div key={index}>
            <Fileview
              exchangeItem={item}
              isMobile={isMobile()}
              tenantEnablePII={false}
              multiSelect={true}
              selectExchangeItem={selectItem}
            />
          </div>
        ))
      )
    }
  }, [items]);

  React.useEffect(() => {
    const columns: IColumn[] = [
      {
        key: 'sender/emailAddress/name',
        name: languageStrings.From,
        minWidth: 150,
        maxWidth: 250,
        isRowHeader: true,
        isResizable: true,
        isSorted: sortColumn === 'sender/emailAddress/name',
        isSortedDescending: sortAscDesc === 'desc',
        data: 'string',
        onColumnClick: _onColumnClick,
        onRender: (item: any) => (
          <Stack horizontal verticalAlign='center' tokens={{ childrenGap: 15 }}>
            {item.sender && <StackItem>
              <Persona
                text={item.sender.emailAddress.name}
                size={PersonaSize.size24}
                hidePersonaDetails={true}
              />
            </StackItem>}
            {item.sender && <StackItem grow>{item.sender.emailAddress.name}</StackItem>}
            {item.sender && item.hasAttachments && <StackItem style={{ textAlign: "right" }}><Icon iconName="Attach" /></StackItem>}
            {!item.sender && <StackItem grow><span style={{ color: "rgb(216, 59, 1)" }}>[{languageStrings.Draft}]</span></StackItem>}
          </Stack>
        ),
      },
      {
        key: 'subject',
        name: languageStrings.Subject,
        minWidth: 250,
        isRowHeader: true,
        isResizable: true,
        isSorted: sortColumn === 'subject',
        isSortedDescending: sortAscDesc === 'desc',
        data: 'string',
        onColumnClick: _onColumnClick,
        onRender: (item: any) => {
          return <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign='center'>
            {item.subject && <StackItem style={{ fontWeight: "500" }}>{item.subject}</StackItem>}
            {!item.subject && <StackItem style={{ fontWeight: "500" }}>({languageStrings.NoSubject})</StackItem>}
            <StackItem style={{ fontSize: "400", paddingRight: "15px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} grow>{item.bodyPreview}</StackItem>
          </Stack>
        },
      },
      {
        key: 'receivedDateTime',
        name: languageStrings.Received,
        fieldName: 'displayName',
        minWidth: 150,
        maxWidth: 150,
        isRowHeader: true,
        isResizable: true,
        isSorted: sortColumn === 'receivedDateTime',
        isSortedDescending: sortAscDesc === 'desc',
        data: 'string',
        onColumnClick: _onColumnClick,
        onRender: (item: any) => (
          <span>{dayjs(item.receivedDateTime).format("L LT")}</span>
        ),
      },
    ];
    setColumns(columns);
  }, [sortColumn, sortAscDesc]);

  React.useEffect(() => {
    if (selectionCleared) {
      _selection.current.setAllSelected(false);
      setSelectedItems(_selection.current.getSelection());
      setSelectionCleared(false);
    }
  }, [selectionCleared]);

  React.useEffect(() => {
    const mainItems: ICommandBarItemProps[] = [];
    const farItems: ICommandBarItemProps[] = [];

    if (moveTo.moveToEnabled && (moveTo.mode === ContentManagerCopyMoveModeEnum.CopySelectedEmails || moveTo.mode === ContentManagerCopyMoveModeEnum.CopyEntireFolderEmails)) {
      mainItems.push({
        key: "cancel",
        text: languageStrings.Cancel,
        iconProps: cancelIcon,
        onClick: () => {
          cancelMove();
        },
      });

      farItems.push(
        {
          key: "selectedFileCountMove",
          text: `${moveTo.mode === ContentManagerCopyMoveModeEnum.CopySelectedEmails ? `${selectedItems.length} ${languageStrings.Selected.toLowerCase()}` : `${languageStrings.EntireFolderSelected}`}`,
          disabled: true,
        }
      )
    } else {
      mainItems.push({
        key: "save",
        disabled: selectedItems?.length === 0 || isLoading,
        text: languageStrings.SaveSelected,
        iconProps: saveSelected,
        onClick: () => {
          if (DragAndDropHelper.isDroppedModeActive) {
            DragAndDropHelper.reset();
          }

          if (selectedItems.length > MAX_SELECTED_FILES) {
            setShowMaxFilesSelectedDialog(true);
          } else {
            dispatch(setContentManagerMoveTo({
              mode: ContentManagerCopyMoveModeEnum.CopySelectedEmails,
              moveToEnabled: true,
              workingDirectories: workingDirectories,
              selectedFiles: [],
              siteUrl: "",
              sourceSiteUrl: "",
              sourceServerRelPath: "",
              busyMovingFiles: false,
              copyJobResults: [],
              isMoveComplete: false,
              isMoveCancelled: false,
              destinationDirectory: {},
              progressCountCompleted: 0,
              progressCountTotal: 0,
              error: ""
            }));

            setShowConfirmMoveToDialog(true);
          }
        },
      });

      mainItems.push(
        {
          key: "saveAll",
          disabled: isLoading || items.messages.length === 0,
          text: languageStrings.SaveEntireFolder,
          iconProps: saveFolder,
          onClick: () => {
            if (DragAndDropHelper.isDroppedModeActive) {
              DragAndDropHelper.reset();
            }
            
            dispatch(setContentManagerExchangeProgress(undefined));
            dispatch(setContentManagerMoveTo({
              mode: ContentManagerCopyMoveModeEnum.CopyEntireFolderEmails,
              moveToEnabled: true,
              workingDirectories: workingDirectories,
              selectedFiles: [],
              siteUrl: "",
              sourceSiteUrl: "",
              sourceServerRelPath: "",
              busyMovingFiles: false,
              copyJobResults: [],
              isMoveComplete: false,
              isMoveCancelled: false,
              destinationDirectory: {},
              progressCountCompleted: 0,
              progressCountTotal: 0,
              error: ""
            }));
            setShowConfirmMoveToDialog(true);
          },
        })

      if (selectedItems?.length > 0) {
        farItems.push(
          {
            key: "selectedFileCount",
            text: `${selectedItems.length} ${languageStrings.Selected.toLowerCase()}`,
            //disabled: waitFor === true,
            iconProps: { iconName: "Clear" },
            onClick: () => {
              setSelectionCleared(true);
            }
          }
        )
      }
    }

    setCommandItems(mainItems);
    setCommandFarItems(farItems);
  }, [selectedItems, isLoading, moveTo, items]);

  const onConfirmMove = React.useCallback(async (): Promise<void> => {
    if (generalModule.isInConentMangerMode()) {
      // getSelectedExchangeEmails(selectedItems.map(x => x.id), exchangeAccount, exchangeFolderId);
      CacheHelper.set(DocubirdCache.selectedExchangeItems, selectedItems);

      if (moveTo.mode === ContentManagerCopyMoveModeEnum.CopySelectedEmails) {
        setIsFetchingAttachments(true);
        try {
          const filesToUpload: { index: number, fileToUpload: IFileToUpload }[] = [];

          await Promise.all(selectedItems.map(async (selectedItem, index) => {
            const fileToUpload: IFileToUpload = {
              id: selectedItem.id,
              ewsId: null,
              name: selectedItem.subject || languageStrings.NoSubject,
              extension: "eml",
              isAttachment: false,
              isSelected: true,
              isDuplicate: false,
              originalName: selectedItem.subject || languageStrings.NoSubject,
              lastModifiedDateTime: selectedItem.receivedDateTime,
              isGraphMailItem: true,
              graphMailItemId: selectedItem.id
            };

            filesToUpload.push({ index, fileToUpload });

            if (selectedItem.hasAttachments) {
              const attachments = await getExchangeMessageAttachments(exchangeAccount, selectedItem.id);
              if (attachments?.length > 0) {
                for (let indexAtt = 0; indexAtt < attachments.length; indexAtt++) {
                  let attachment = attachments[indexAtt];
                  const attachmentToUpload: IFileToUpload = {
                    id: attachment.id,
                    ewsId: null,
                    name: removeExtension(attachment.name),
                    extension: getFileExtension(attachment.name),
                    isAttachment: true,
                    isSelected: autoSelectAttachments,
                    isDuplicate: false,
                    originalName: removeExtension(attachment.name),
                    lastModifiedDateTime: attachment.lastModifiedDateTime,
                    isGraphMailItem: true,
                    graphMailItemId: selectedItem.id
                  }

                  filesToUpload.push({ index: index + ((indexAtt + 1) / 100), fileToUpload: attachmentToUpload });
                }
              }
            }
          }));

          dispatch(addUploadedFiles(filesToUpload.sort((a, b) => a.index - b.index).map(f => f.fileToUpload)));
        } catch (error) {
          logger(error, "ExchangeContent.tsx (onComfirmMove)");
        }
        setIsFetchingAttachments(false);
      }
    } else {
      setIsMultipleFilesSelectedPanelOpen(false);
      dispatch(setContentManagerMoveTo({
        mode: ContentManagerCopyMoveModeEnum.CopySelectedEmails,
        moveToEnabled: true,
        workingDirectories: [],
        selectedFiles: [],
        siteUrl: "",
        sourceSiteUrl: "",
        sourceServerRelPath: "",
        busyMovingFiles: false,
        copyJobResults: [],
        isMoveComplete: false,
        isMoveCancelled: false,
        destinationDirectory: {},
        progressCountCompleted: 0,
        progressCountTotal: 0,
        error: ""
      }));
      dispatch(setFiles(files.map(file => {
        return {
          ...file,
          isSelected: !file.isGraphMailItem ? false : file.isSelected
        }
      })));
    }

    setShowConfirmMoveToDialog(false);
    dispatch(setContentManagerSearchValue(""));
    dispatch(resetWorkingDirectories());
    dispatch(resetContentManagerDirectory());
  }, [selectedItems, moveTo, exchangeAccount, files, autoSelectAttachments]);

  const getFileExtension = (name: string) => {
    if (name?.includes(".")) {
      const fileParts = name.split(".");
      if (fileParts?.length > 1) {
        return fileParts[fileParts.length - 1];
      }
    }    
    return "";
  }

  const cancelMove = React.useCallback(() => {
    if (generalModule.isInConentMangerMode()) {
      dispatch(setFiles([]));
    } else {
      dispatch(setFiles(files.filter(x => !x.isGraphMailItem).map(file => {
        return {
          ...file,
          isSelected: true
        }
      })));

      setItems(items => { return { messages: items.messages.map(msg => { return { ...msg, selected: false } }), nextLink: items.nextLink } });
    }

    dispatch(setContentManagerMoveTo({
      moveToEnabled: false,
      workingDirectories: [],
      selectedFiles: [],
      siteUrl: "",
      sourceServerRelPath: "",
      progressCountCompleted: 0,
      progressCountTotal: 0,
      error: ""
    }));

    setShowConfirmMoveToDialog(false);
  }, [files]);

  const _getKey = (item: any, index?: number): string => {
    return item?.key;
  };

  const _onColumnClick = (ev: React.MouseEvent<HTMLElement>, column: IColumn): void => {
    if (column.key === sortColumn) {
      sortAscDesc === "asc" ? setSortAscDesc("desc") : setSortAscDesc("asc")
    } else {
      setSortColumn(column.key);
      setSortAscDesc("desc");
    }

    setUpdateEmails(new Date().toUTCString());
  };

  const _onRenderDetailsFooter = (detailsFooterProps: IDetailsFooterProps): JSX.Element => {
    return (
      <>
        {!isLoading && items.messages.length === 0 && <Stack verticalAlign="center" horizontalAlign="center" styles={noResultsStackStyles}>
          <StackItem align="center">
            <img src={searchIconSrc} />
          </StackItem>
          <StackItem align="center">
            <Label disabled>{languageStrings.NoResultsFound}</Label>
          </StackItem>
        </Stack>}
      </>
    );
  }

  const onRenderCustomPlaceholder = (rowProps: IDetailsRowProps, index?: number, defaultRender?: (props: IDetailsRowProps) => React.ReactNode) => {
    if (rowProps.item === null) {
      fetchNextPageEmails();
    }
    return defaultRender(rowProps);
  }

  return (
    <>
      {generalModule.isInConentMangerMode() && <>
        <CommandBar
          styles={commandBarStyle}
          items={commandItems}
          farItems={commandFarItems}
          ariaLabel="Use left and right arrow keys to navigate between commands"
        />
        <Stack horizontal>
          <Stack.Item grow>
            <div className={classNames.wrapper}>
              <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto}>
                <ShimmeredDetailsList
                  compact={false}
                  items={items.messages}
                  columns={columns}
                  selectionMode={SelectionMode.multiple}
                  constrainMode={ConstrainMode.unconstrained}
                  getKey={_getKey}
                  enableShimmer={isLoading}
                  layoutMode={DetailsListLayoutMode.justified}
                  isHeaderVisible={true}
                  selectionPreservedOnEmptyClick={true}
                  selection={_selection.current}
                  ariaLabelForSelectionColumn="Toggle selection"
                  ariaLabelForSelectAllCheckbox="Toggle selection for all items"
                  checkButtonAriaLabel="Row checkbox"
                  onRenderDetailsFooter={_onRenderDetailsFooter}
                  onRenderCustomPlaceholder={onRenderCustomPlaceholder}
                />
              </ScrollablePane>
            </div>
          </Stack.Item>
          {selectedItems?.length === 1 && <Stack.Item>
            <ExchangePreview mailboxId={exchangeAccount} messageId={selectedItems[0].id} initialWidth={650} minWidth={500} />
          </Stack.Item>}
        </Stack>
      </>}
      {!generalModule.isInConentMangerMode() &&
        <div>
          <ExpanderWithSubtitle
            items={fileViews}
            itemsSelected={items.messages.filter(x => x.selected).length}
            title={languageStrings.Emails}
            selectedItemsMoreClicked={() => setIsMultipleFilesSelectedPanelOpen(true)}
          />
        </div>
      }
      {isMultipleFilesSelectedPanelOpen && (
        <WebPanel
          isOpen={isMultipleFilesSelectedPanelOpen}
          closePanel={() => setIsMultipleFilesSelectedPanelOpen(false)}
          headerText={`${files.filter(x => x.isSelected && x.isGraphMailItem).length} ${files.filter(x => x.isSelected && x.isGraphMailItem).length === 1
            ? languageStrings.FileSelected.toLowerCase()
            : languageStrings.FilesSelected.toLowerCase()
            }`}
          styles={{ main: { overflow: "unset" } }}
        >
          <MultipleFilesSeleted

            files={files.filter(x => x.isSelected && x.isGraphMailItem)}
            isGraphQuery={false}
            currentDirectory={currentDirectory}
            workingDirectories={workingDirectories}
            openConfirmSave={() => {
              if (files.filter(x => x.isSelected && x.isGraphMailItem).length > MAX_SELECTED_FILES) {
                setShowMaxFilesSelectedDialog(true);
              } else {
                setShowConfirmMoveToDialog(true);
              }
            }}
            setIsMultipleFilesSelectedPanelOpen={setIsMultipleFilesSelectedPanelOpen}
          />
        </WebPanel>
      )}

      {showConfirmMoveToDialog && <CMConfirmModal
        title={languageStrings.SaveEmails}
        message={languageStrings.SaveEmailsConfirmBody}
        showDialog={showConfirmMoveToDialog}
        isBusy={isFetchingAttachments}
        closeDialog={cancelMove}
        closeText=""
        confirmText={isFetchingAttachments ? languageStrings.DownloadingAttachments : languageStrings.Ok}
        onConfirm={onConfirmMove}
      />}
      {showMaxFilesSelectedDialog && <CMConfirmModal
        title={languageStrings.LimitReached}
        message={languageStrings.MaximumEmailMessagesSelected.replace("{amount}", MAX_SELECTED_FILES.toString())}
        showDialog={showMaxFilesSelectedDialog}
        closeDialog={() => {
          setShowMaxFilesSelectedDialog(false);
        }}
        closeText=""
        confirmText={languageStrings.Ok}
        onConfirm={() => {
          setShowMaxFilesSelectedDialog(false);
        }}
      />}
    </>
  );
};